/**
 * Reserves bottom-of-page space for the floating "selection actions" bar so the
 * fixed-position bar does not occlude the data-table pagination underneath it.
 *
 * Mechanism: while at least one bar is active, the body has the
 * `has-floating-actions-bar` class. A global CSS rule (in App.vue) translates
 * that class into `padding-bottom` on the body, pushing the bottom of normal-
 * flow content above the floating bar.
 */

const BODY_CLASS = 'has-floating-actions-bar';

let activeBars = 0;

export function reserveFloatingBarInset(): void {
  if (activeBars === 0 && typeof document !== 'undefined' && document.body) {
    document.body.classList.add(BODY_CLASS);
  }
  activeBars += 1;
}

export function releaseFloatingBarInset(): void {
  if (activeBars <= 0) {
    activeBars = 0;
    return;
  }
  activeBars -= 1;
  if (activeBars === 0 && typeof document !== 'undefined' && document.body) {
    document.body.classList.remove(BODY_CLASS);
  }
}

/** Test-only: reset the counter between specs. Do not call from production code. */
export function __resetForTests(): void {
  activeBars = 0;
  if (typeof document !== 'undefined' && document.body && document.body.classList) {
    document.body.classList.remove(BODY_CLASS);
  }
}

/**
 * Per-instance reservation. Components owning a floating bar should hold one
 * of these and call `syncFromCount(n)` from a watcher on their selection count
 * and `release()` in `beforeDestroy`.
 */
export class FloatingBarInsetReservation {
  private hasReserved = false;

  public syncFromCount(count: number): void {
    if (count > 0 && !this.hasReserved) {
      reserveFloatingBarInset();
      this.hasReserved = true;
    } else if (count <= 0 && this.hasReserved) {
      releaseFloatingBarInset();
      this.hasReserved = false;
    }
  }

  public release(): void {
    if (this.hasReserved) {
      releaseFloatingBarInset();
      this.hasReserved = false;
    }
  }
}
